import React from 'react';
import { Layers, Users } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { SearchableMultiSelect } from '../ui/searchable-multi-select';

export interface SelectOption {
  value: number;
  label: string;
  description?: string;
}

export interface AddContactsDialogProps {
  open: boolean;
  loading: boolean;
  contactsToAdd: number[];
  batchesToAdd: number[];
  batchOptions: SelectOption[];
  contactOptions: SelectOption[];
  batchesLoading: boolean;
  contactsLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onContactsChange: (ids: number[]) => void;
  onBatchesChange: (ids: number[]) => void;
  onSubmit: () => void;
  emptyContactsMessage?: string;
}

export function AddContactsDialog({
  open,
  loading,
  contactsToAdd,
  batchesToAdd,
  batchOptions,
  contactOptions,
  batchesLoading,
  contactsLoading,
  onOpenChange,
  onContactsChange,
  onBatchesChange,
  onSubmit,
  emptyContactsMessage
}: AddContactsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar Contatos</DialogTitle>
          <DialogDescription>
            Selecione lotes ou contatos individuais para adicionar a campanha.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Batch Selection */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Layers className="h-4 w-4" />
              Lotes de Contatos
            </div>
            <SearchableMultiSelect
              options={batchOptions}
              selected={batchesToAdd}
              onSelectionChange={(ids) => onBatchesChange(ids as number[])}
              placeholder="Selecionar lotes..."
              searchPlaceholder="Buscar lotes..."
              emptyMessage="Nenhum lote encontrado."
              loading={batchesLoading}
              loadingMessage="Carregando lotes..."
            />
          </div>

          {/* Individual Contacts */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" />
              Contatos Individuais
            </div>
            <SearchableMultiSelect
              options={contactOptions}
              selected={contactsToAdd}
              onSelectionChange={(ids) => onContactsChange(ids as number[])}
              placeholder="Selecionar contatos..."
              searchPlaceholder="Buscar por nome ou telefone..."
              emptyMessage={emptyContactsMessage || 'Nenhum contato encontrado.'}
              loading={contactsLoading}
              loadingMessage="Carregando contatos..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={loading || (contactsToAdd.length === 0 && batchesToAdd.length === 0)}
          >
            {loading ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
